import { Menu, MenuItem, MenuList, Typography } from '@mui/material'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { isNil } from 'es-toolkit'

import { exportLayer } from 'api/field'
import { requestCreateHarvests } from 'api/harvest'
import { useDispatch, useSelector } from 'react-redux'

import { downloadingLayer } from 'actions/field'

import AwaitingLayerDownload from 'components/harvest/layer-download/awaiting-download'
import HarvestLogDownload from 'components/harvest/layer-download/harvest-log-download'
import DownloadUbjOptions, {
  type DownloadUbjOptions as DownloadUbjOptionsType,
} from 'components/harvest/layer-download/download-ubj-options'

import { getTranslatedName } from 'common/misc'

//Icons
import {
  FarmtrxIconEditCropCalibration,
  FarmtrxIconUpdateTotalYield as EditTotalYield,
  FarmtrxIconEditTimeDelay as EditTimeDelay,
  FarmtrxIconUpdateTestWeight as UpdateTestWeight,
  FarmtrxIconEditSensorCalibration as EditSensorCalibration,
  FarmtrxIconHeader,
  FarmtrxIconRefresh,
  FarmtrxIconExportHarvest as CloudDownloadIcon,
  FarmtrxIconCrop,
  FarmtrxIconMinus,
  FarmtrxIconWheat2,
} from '@farmtrx/farmtrx-icons'

//Types

import { type HarvestModel } from 'state/harvest'

import { type HarvestPermissions } from './harvest-panel'

import { type AppState } from 'state/index'

import { type FieldModel } from 'state/field'

import { showModal } from 'actions/app'
import { TEST_IDS } from 'test-ids'

export type HarvestMenuProps = {
  anchorEl: null | HTMLElement
  permissions: HarvestPermissions
  onClickHandler: Function
  open: boolean
  harvest: HarvestModel
  showExportModal: Function
  onClose: Function
}
const iconSize = 16

const iconStyle = { margin: 4, height: iconSize, width: iconSize }
const textStyle = { fontSize: '.75rem' }

export default function HarvestSettingsPanel(props: HarvestMenuProps) {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const field = useSelector((state: AppState) => state.field.singleton)
  const selectedYear = useSelector((state: AppState) => state.field.year)
  const fields: FieldModel[] = useSelector(
    (state: AppState) => state.field.collection,
  )
  //change harvest year was chosen as it's one of the permissions only a FarmTRX Admin will have
  //other one is download ubj. This combined with individual permissions will determine what is shown,
  //If this is true, and the user has the relevant permission the item is shown. This is not checked for
  //export harvest, as it's always shown, and changeHarvestYear or downloadUbj as they are shown based on
  //the user being a FarmTRX Admin. The permissions are checked before each menu items is added to the menu.
  const enableControls =
    props.harvest.cropYear >= new Date().getFullYear() - 1 ||
    props.permissions.changeHarvestYear
  const handleOnClick = useCallback(
    async (name) => {
      if (name === 'create_harvests') {
        await dispatch(
          requestCreateHarvests(props.harvest.fieldId, selectedYear) as any,
        )
      } else if (name !== 'export_harvest') {
        props.onClickHandler(name, props.harvest)
      } else {
        await dispatch(
          downloadingLayer({
            url: null,
            fileName: '',
          }),
        )
        props.showExportModal(props.harvest)
      }
      props.onClose()
    },
    [
      dispatch,
      props.harvest,
      props.onClickHandler,
      props.onClose,
      props.showExportModal,
      selectedYear,
    ],
  )

  const handleUbjConfirm = useCallback(
    async (options: DownloadUbjOptionsType) => {
      let fieldForHarvest: FieldModel | null | undefined = null
      if (field?.id === props.harvest.fieldId) {
        fieldForHarvest = field
      } else {
        fieldForHarvest = fields.find((f) => {
          return f.id === props.harvest.fieldId
        })
      }
      if (fieldForHarvest) {
        await dispatch(
          downloadingLayer({
            url: null,
            fileName: '',
          }),
        )
        const data = {
          tenant_id: props.harvest.tenantId,
          export_type: 'ubj',
          field_name: fieldForHarvest.name,
          year: props.harvest.cropYear,
          include_shp: options.include_shp,
          include_json: options.include_json,
          include_logs: options.include_logs,
        }
        //@ts-expect-error
        dispatch(exportLayer(props.harvest.id, data))
        dispatch(
          showModal(<AwaitingLayerDownload />, {
            title: t('downloading_ubj_title', {
              year: props.harvest.cropYear,
              crop: getTranslatedName(props.harvest.crop.name),
            }),
          }),
        )
      }
    },
    [dispatch, field, fields, props.harvest, t],
  )

  const onDownloadUbj = useCallback(async () => {
    let fieldForHarvest: FieldModel | null | undefined = null
    if (field?.id === props.harvest.fieldId) {
      fieldForHarvest = field
    } else {
      fieldForHarvest = fields.find((f) => {
        return f.id === props.harvest.fieldId
      })
    }
    if (fieldForHarvest) {
      props.onClose()
      dispatch(
        showModal(<DownloadUbjOptions onConfirm={handleUbjConfirm} />, {
          title: t('download_ubj_options'),
        }),
      )
    }
  }, [
    dispatch,
    field,
    fields,
    props.harvest,
    props.onClose,
    handleUbjConfirm,
    t,
  ])

  const onDownloadHarvestLog = useCallback(() => {
    props.onClose()
    dispatch(
      showModal(<HarvestLogDownload harvestId={props.harvest.id} />, {
        title: t('harvest_log_title', {
          year: props.harvest.cropYear,
          crop: getTranslatedName(props.harvest.crop.name),
        }),
      }),
    )
  }, [dispatch, props.harvest, props.onClose, t])

  return (
    <Menu
      anchorEl={props.anchorEl}
      open={props.open}
      anchorOrigin={{ vertical: -12, horizontal: 50 }}
      id={TEST_IDS.harvestSettings}
      data-testid={TEST_IDS.harvestSettings}
      slotProps={{
        list: { 'aria-labelledby': TEST_IDS.harvestSettingsButton },
      }}
      onClose={() => props.onClose(null)}
      disableEnforceFocus={true}
      closeAfterTransition={false}
    >
      <MenuList style={{ outline: 0 }}>
        {props.permissions.setHeader && enableControls && (
          <MenuItem
            id={TEST_IDS.setHeader}
            data-testid={TEST_IDS.setHeader}
            onClick={() => {
              handleOnClick('set_header')
            }}
          >
            <FarmtrxIconHeader style={iconStyle} />
            <Typography sx={textStyle}>
              {t('set_header').toUpperCase()}
            </Typography>
          </MenuItem>
        )}
        {props.permissions.editCropType && enableControls && (
          <MenuItem
            id={TEST_IDS.editCropType}
            data-testid={TEST_IDS.editCropType}
            onClick={(e) => handleOnClick('edit_crop_type')}
          >
            <FarmtrxIconCrop style={iconStyle} />
            <Typography sx={textStyle}>
              {t('edit_crop_type').toUpperCase()}
            </Typography>
          </MenuItem>
        )}
        {props.permissions.updateTotalYield && enableControls && (
          <MenuItem
            id={TEST_IDS.changeTotalYield}
            data-testid={TEST_IDS.changeTotalYield}
            onClick={(e) => handleOnClick('change_ty')}
          >
            <EditTotalYield style={iconStyle} />
            <Typography sx={textStyle}>
              {t('change_ty').toUpperCase()}
            </Typography>
          </MenuItem>
        )}
        {props.permissions.updateTestWeight && enableControls && (
          <MenuItem
            id={TEST_IDS.changeTestWeight}
            data-testid={TEST_IDS.changeTestWeight}
            onClick={() => handleOnClick('change_tw')}
          >
            <UpdateTestWeight style={iconStyle} />
            <Typography sx={textStyle}>
              {t('change_tw').toUpperCase()}
            </Typography>
          </MenuItem>
        )}
        {props.permissions.editSensorCalibration && enableControls && (
          <MenuItem
            id={TEST_IDS.editSensorCalibration}
            data-testid={TEST_IDS.editSensorCalibration}
            onClick={(e) => handleOnClick('edit_sensor_calibration')}
          >
            <EditSensorCalibration style={iconStyle} />
            <Typography style={{ fontSize: '.75rem' }}>
              {t('edit_sensor_calibration').toUpperCase()}
            </Typography>
          </MenuItem>
        )}
        {props.permissions.editCropCalibration && enableControls && (
          <MenuItem
            id={TEST_IDS.editCropCalibration}
            data-testid={TEST_IDS.editCropCalibration}
            onClick={() => handleOnClick('edit_crop_calibration')}
          >
            <FarmtrxIconEditCropCalibration style={iconStyle} />
            <Typography sx={textStyle}>
              {t('edit_crop_calibration').toUpperCase()}
            </Typography>
          </MenuItem>
        )}
        {props.permissions.exportHarvest && (
          <MenuItem
            id={TEST_IDS.exportHarvest}
            data-testid={TEST_IDS.exportHarvest}
            onClick={() => handleOnClick('export_harvest')}
          >
            <CloudDownloadIcon color="primary" style={iconStyle} />
            <Typography sx={textStyle}>
              {t('export_harvest').toUpperCase()}
            </Typography>
          </MenuItem>
        )}
        {props.permissions.editTimeDelay && enableControls && (
          <MenuItem
            id={TEST_IDS.editTimeDelay}
            data-testid={TEST_IDS.editTimeDelay}
            onClick={(e) => handleOnClick('edit_time_delay')}
          >
            <EditTimeDelay style={iconStyle} />
            <Typography sx={textStyle}>
              {t('edit_time_delay').toUpperCase()}
            </Typography>
          </MenuItem>
        )}
        {props.permissions.refreshHarvest && enableControls && (
          <MenuItem
            id={TEST_IDS.refreshHarvest}
            data-testid={TEST_IDS.refreshHarvest}
            onClick={() => handleOnClick('refresh')}
          >
            <FarmtrxIconRefresh style={iconStyle} />
            <Typography sx={textStyle}>{t('refresh').toUpperCase()}</Typography>
          </MenuItem>
        )}
        {props.permissions.downloadUbj && (
          <MenuItem
            id={TEST_IDS.downloadUbj}
            data-testid={TEST_IDS.downloadUbj}
            onClick={() => onDownloadUbj()}
          >
            <CloudDownloadIcon color="primary" style={iconStyle} />
            <Typography sx={textStyle}>{'DOWNLOAD UBJS'}</Typography>
          </MenuItem>
        )}
        {props.permissions.downloadHarvestLog && (
          <MenuItem
            id={TEST_IDS.downloadHarvestLog}
            data-testid={TEST_IDS.downloadHarvestLog}
            onClick={() => onDownloadHarvestLog()}
          >
            <CloudDownloadIcon color="primary" style={iconStyle} />
            <Typography sx={textStyle}>
              {t('download_harvest_log').toUpperCase()}
            </Typography>
          </MenuItem>
        )}
        {props.permissions.changeHarvestYear && (
          <MenuItem
            id={TEST_IDS.changeHarvestYear}
            data-testid={TEST_IDS.changeHarvestYear}
            onClick={(e) => handleOnClick('change_harvest_year')}
          >
            <FarmtrxIconMinus style={iconStyle} />
            <Typography sx={textStyle}>
              {t('change_harvest_year').toUpperCase()}
            </Typography>
          </MenuItem>
        )}
        {props.permissions.createHarvests && (
          <MenuItem
            id={TEST_IDS.createHarvests}
            data-testid={TEST_IDS.createHarvests}
            disabled={isNil(selectedYear)}
            onClick={() => handleOnClick('create_harvests')}
          >
            <FarmtrxIconWheat2 style={iconStyle} />
            <Typography sx={textStyle}>
              {t('create_harvests').toUpperCase()}
            </Typography>
          </MenuItem>
        )}
      </MenuList>
    </Menu>
  )
}
